const BASE62_ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

export const DIFF_ANCHOR_LENGTH_3_THRESHOLD = 1200;
export const DIFF_ANCHOR_LENGTH_5_MIN_BLOCKS = 25000;
export const DIFF_ANCHOR_LENGTH_5_MIN_PROBES = 512;

const ANCHOR_ID_MIN_LENGTH = 3;
const ANCHOR_ID_MAX_LENGTH = 5;
const DIGEST_LENGTH = 12;

const BLOCK_ANCHOR_NODE_TYPES = new Set([
  'paragraph',
  'heading',
  'listItem',
  'bulletList',
  'orderedList',
  'taskList',
  'taskItem',
  'blockquote',
  'codeBlock',
  'image'
]);

const clampAnchorLength = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return ANCHOR_ID_MIN_LENGTH;
  return Math.max(ANCHOR_ID_MIN_LENGTH, Math.min(ANCHOR_ID_MAX_LENGTH, Math.floor(numeric)));
};

const fnv1aHash = (value) => {
  const input = String(value ?? '');
  let hash = 2166136261;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return hash >>> 0;
};

const buildBase62Digest = (seed, length = DIGEST_LENGTH) => {
  let state = fnv1aHash(seed);
  if (!state) state = 2166136261;

  let out = '';
  for (let index = 0; index < length; index += 1) {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    out += BASE62_ALPHABET[state % BASE62_ALPHABET.length];
  }
  return out;
};

const buildCandidateWindows = (digest, width) => {
  const safeDigest = String(digest || '');
  const safeWidth = clampAnchorLength(width);
  if (safeDigest.length < safeWidth) {
    return [safeDigest.padEnd(safeWidth, '0').slice(0, safeWidth)];
  }

  const windows = [];
  for (let index = 0; index <= safeDigest.length - safeWidth; index += 1) {
    windows.push(safeDigest.slice(index, index + safeWidth));
  }
  return windows;
};

const isValidAnchorId = (value) => {
  const text = String(value || '').trim();
  if (!text) return false;
  if (text.length < ANCHOR_ID_MIN_LENGTH || text.length > ANCHOR_ID_MAX_LENGTH) return false;
  return /^[0-9A-Za-z]+$/.test(text);
};

const normalizeAnchorId = (value) => {
  const text = String(value || '').trim();
  return isValidAnchorId(text) ? text : '';
};

export const isBlockAnchorEligibleNode = (node) => {
  return Boolean(node && typeof node === 'object' && BLOCK_ANCHOR_NODE_TYPES.has(String(node.type || '').trim()));
};

const getNodeTextSnippet = (node, maxLength = 120) => {
  if (!node || typeof node !== 'object') return '';
  if (typeof node.text === 'string') return node.text.slice(0, maxLength);

  const parts = [];
  const stack = Array.isArray(node.content) ? [...node.content] : [];
  while (stack.length > 0 && parts.join('').length < maxLength) {
    const current = stack.shift();
    if (!current || typeof current !== 'object') continue;
    if (typeof current.text === 'string' && current.text.trim()) {
      parts.push(current.text);
      continue;
    }
    if (Array.isArray(current.content) && current.content.length > 0) {
      stack.unshift(...current.content);
    }
  }
  return parts.join(' ').slice(0, maxLength);
};

const countEligibleBlockNodes = (docJson) => {
  const content = Array.isArray(docJson?.content) ? docJson.content : [];
  let count = 0;

  const visit = (node) => {
    if (!node || typeof node !== 'object') return;
    if (isBlockAnchorEligibleNode(node)) count += 1;
    const children = Array.isArray(node.content) ? node.content : [];
    children.forEach(visit);
  };

  content.forEach(visit);
  return count;
};

export const resolveAnchorIdLength = ({ blockNodeCount = 0, probeAttempts = 0 } = {}) => {
  const normalizedCount = Number(blockNodeCount) || 0;
  const normalizedProbeAttempts = Number(probeAttempts) || 0;

  if (
    normalizedCount >= DIFF_ANCHOR_LENGTH_5_MIN_BLOCKS
    && normalizedProbeAttempts >= DIFF_ANCHOR_LENGTH_5_MIN_PROBES
  ) {
    return 5;
  }

  return normalizedCount >= DIFF_ANCHOR_LENGTH_3_THRESHOLD ? 4 : 3;
};

export const createAnchorIdAllocator = ({ docId = null, existingIds = [], blockNodeCount = 0 } = {}) => {
  const used = new Set(
    Array.from(existingIds || [])
      .map(id => normalizeAnchorId(id))
      .filter(Boolean)
  );

  const docSeed = String(docId || 'doc');

  const allocate = ({ fingerprint = '', preferredLength } = {}) => {
    const baseLength = clampAnchorLength(preferredLength || resolveAnchorIdLength({ blockNodeCount }));
    let probeAttempts = 0;

    while (probeAttempts < 4096) {
      const activeLength = preferredLength
        ? clampAnchorLength(preferredLength)
        : resolveAnchorIdLength({ blockNodeCount, probeAttempts });

      const digest = buildBase62Digest(`${docSeed}|${fingerprint}|${probeAttempts}`, DIGEST_LENGTH);
      const candidates = buildCandidateWindows(digest, activeLength);

      for (const candidate of candidates) {
        if (!used.has(candidate)) {
          used.add(candidate);
          return candidate;
        }
      }

      probeAttempts += 1;
    }

    const emergencyDigest = buildBase62Digest(`${docSeed}|${fingerprint}|emergency`, DIGEST_LENGTH + 4);
    const emergencyLength = Math.max(baseLength, 5);
    const emergencyCandidates = buildCandidateWindows(emergencyDigest, emergencyLength);
    for (const candidate of emergencyCandidates) {
      if (!used.has(candidate)) {
        used.add(candidate);
        return candidate;
      }
    }

    throw new Error('Unable to allocate a unique node anchor id.');
  };

  return {
    allocate,
    has(id) {
      return used.has(normalizeAnchorId(id));
    },
    add(id) {
      const normalized = normalizeAnchorId(id);
      if (!normalized) return false;
      if (used.has(normalized)) return false;
      used.add(normalized);
      return true;
    },
    snapshot() {
      return new Set(used);
    }
  };
};

export const ensureDocAnchorIds = (docJson, options = {}) => {
  if (!docJson || typeof docJson !== 'object' || docJson.type !== 'doc') {
    return {
      json: docJson,
      changed: false,
      generatedCount: 0,
      healedCount: 0,
      blockNodeCount: 0
    };
  }

  const clone = JSON.parse(JSON.stringify(docJson));
  const blockNodeCount = countEligibleBlockNodes(clone);
  const allocator = createAnchorIdAllocator({
    docId: options?.docId,
    blockNodeCount
  });

  let changed = false;
  let generatedCount = 0;
  let healedCount = 0;

  const visit = (node, path = '') => {
    if (!node || typeof node !== 'object') return;

    if (isBlockAnchorEligibleNode(node)) {
      const attrs = node.attrs && typeof node.attrs === 'object' ? node.attrs : {};
      const existing = normalizeAnchorId(attrs.node_anchor_id);

      if (existing && allocator.add(existing)) {
        if (!node.attrs || typeof node.attrs !== 'object') {
          node.attrs = { ...attrs, node_anchor_id: existing };
          changed = true;
        }
      } else {
        const fingerprint = `${path}|${String(node.type || '')}|${getNodeTextSnippet(node)}`;
        const generated = allocator.allocate({ fingerprint });
        node.attrs = {
          ...attrs,
          node_anchor_id: generated
        };
        changed = true;
        generatedCount += existing ? 0 : 1;
        healedCount += existing ? 1 : 0;
      }
    }

    const children = Array.isArray(node.content) ? node.content : [];
    children.forEach((child, index) => {
      const nextPath = path ? `${path}.${index}` : String(index);
      visit(child, nextPath);
    });
  };

  const topContent = Array.isArray(clone.content) ? clone.content : [];
  topContent.forEach((node, index) => visit(node, String(index)));

  return {
    json: clone,
    changed,
    generatedCount,
    healedCount,
    blockNodeCount
  };
};
